// cards_menu_styles.js (versión corregida)
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  IconButton,
  Popover
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import BackgroundImageSelectionDialog from "../image_components/background_image_selection_dialog";
import { HexColorPicker } from "react-colorful";

// --- ESTILOS COMPARTIDOS ---
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#ffffff",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  p: 4,
  borderRadius: "24px",
  minWidth: "360px",
  maxWidth: "90vw",
  outline: 'none',
  border: '1px solid rgba(0,0,0,0.05)',
  maxHeight: '90vh',
  overflowY: 'auto'
};

const actionButtonStyle = {
  backgroundColor: '#ff0000',
  color: '#ffffff',
  fontWeight: 700,
  py: 1.5,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: '1rem',
  mt: 3,
  boxShadow: '0 4px 12px rgba(255, 0, 0, 0.3)',
  '&:hover': { backgroundColor: '#cc0000', boxShadow: '0 6px 16px rgba(255, 0, 0, 0.4)' }
};

const inputContainerStyle = {
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between', 
    mb: 2, 
    p: 1.5, 
    borderRadius: '12px', 
    border: '1px solid #f0f0f0',
    backgroundColor: '#fff'
};

// --- COMPONENTE: SELECTOR DE COLOR PERSONALIZADO ---
const CustomColorPicker = ({ label, color, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <Box sx={inputContainerStyle}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>{label}</Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="caption" sx={{ color: '#999', fontFamily: 'monospace', textTransform: 'uppercase' }}>
            {color}
        </Typography>

        <Box 
            onClick={handleClick}
            sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%', 
                bgcolor: color, 
                border: '2px solid #fff',
                boxShadow: '0 0 0 1px #ddd',
                cursor: 'pointer',
                transition: 'transform 0.1s',
                '&:hover': { transform: 'scale(1.1)' }
            }} 
        />
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
            sx: { p: 2, borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }
        }}
      >
        <HexColorPicker color={color} onChange={onChange} />
      </Popover>
    </Box>
  );
};

// --- COMPONENTE PRINCIPAL ---
const CardsMenuStyles = ({ open, onClose, onSave, section, restaurantId }) => {
  const [isBgDialogOpen, setIsBgDialogOpen] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(section.background_image_url || "");
  const [buttonsColor, setButtonsColor] = useState(
    section.styles?.buttons_color || section.button_color_cards || "#ff0000"
  );

  // Sincronizar cuando cambia la sección (por si se abre con otra sección)
  useEffect(() => {
    setBackgroundImageUrl(section.background_image_url || "");
    setButtonsColor(section.styles?.buttons_color || section.button_color_cards || "#ff0000");
  }, [section]);

  const handleSave = () => {
    onSave(section.order, section.section_type || "default", {
      styles: { buttons_color: buttonsColor },
      background_image_url: backgroundImageUrl
    });
    onClose();
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          {/* Header del Modal */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6" sx={{ color: "#000", fontWeight: 800 }}>
              Estilos de Sección: {section.name}
            </Typography>
            <IconButton onClick={onClose} size="small" sx={{ color: '#666' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Sección de Imagen de Fondo */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block', fontWeight: 600 }}>
              IMAGEN DE CABECERA
            </Typography>
            
            <Box
              sx={{
                width: "100%",
                height: 140,
                borderRadius: "16px",
                backgroundImage: `url(${backgroundImageUrl || "https://via.placeholder.com/400x120?text=Sin+Imagen"})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: "1px solid #eee",
              }}
            >
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.3)', transition: '0.2s', '&:hover': { bgcolor: 'rgba(0,0,0,0.5)' } }} />
                
                <Button
                    onClick={() => setIsBgDialogOpen(true)}
                    startIcon={<EditIcon />}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: 'white',
                        borderColor: 'white',
                        border: '1px solid rgba(255,255,255,0.8)',
                        backdropFilter: 'blur(4px)',
                        bgcolor: 'rgba(0,0,0,0.2)',
                        borderRadius: '20px',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': { bgcolor: 'white', color: 'black', borderColor: 'white' }
                    }}
                >
                    Cambiar Imagen
                </Button>
            </Box>
          </Box>

          {/* Selector de color único */}
          <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase' }}>
            COLOR DE BOTONES Y DETALLES
          </Typography>
          
          <CustomColorPicker
            label="Botones y Detalles"
            color={buttonsColor}
            onChange={setButtonsColor}
          />

          {/* Botón Guardar */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleSave}
            sx={actionButtonStyle}
          >
            Guardar Cambios
          </Button>
        </Box>
      </Modal>

      <BackgroundImageSelectionDialog
        open={isBgDialogOpen}
        handleClose={() => setIsBgDialogOpen(false)}
        onSelectImage={(url) => {
          setBackgroundImageUrl(url.image_url);
          setIsBgDialogOpen(false);
        }}
        restaurantId={restaurantId}
      />
    </>
  );
};

export default CardsMenuStyles;